/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import React, { createContext, useState, useCallback, useEffect } from 'react';
import type { FC } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';
import isEqual from 'lodash/isEqual';

import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { usePageContentId } from '@confluence/page-context';
import type { RetrieveEditorTitleAndContent } from '@confluence/editor-features/entry-points/useRetrieveEditorTitleAndContent.ts';
import type { PublishProps } from '@confluence/editor-features/entry-points/types.ts';
import type { OverrideMovePageParams } from '@confluence/page-tree/entry-points/useMovePageHandler.ts';
import { usePubSubEvent } from '@confluence/pubsub-client/entry-points/usePubSubEvent.ts';
import {
	useFetchRestrictionsRESTObj,
	restrictionsArePrivateToYouOnly,
} from '@confluence/restrictions/entry-points/RestrictionsREST.ts';
import type { RestrictionsRESTObj } from '@confluence/restrictions/entry-points/RestrictionsREST.ts';
import { fg } from '@confluence/feature-gating';
import { useSubscriptionEvent } from '@confluence/graphql/entry-points/useSubscriptionEvent.ts';
import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations.ts';
import { expVal } from '@confluence/feature-experiments';

import type {
	PublishModalContextPublishModalWithMoreOptionsQuery as PublishModalContextPublishModalWithMoreOptionsQuery$data,
	PublishModalContextPublishModalWithMoreOptionsQueryVariables as PublishModalContextPublishModalWithMoreOptionsQuery$variables,
} from './__types__/PublishModalContextPublishModalWithMoreOptionsQuery.ts';
import { convertRestrictionsObjFromSchedulePublishInfo } from './publishHelpers.tsx';

// These properties are the parts of the publish modal's state
// that represent the new settings the user picked to be applied
// to the page upon publish.
export type PublishModalContextSettings = {
	pendingScheduledPublishDate: Date | undefined;
	versionComments: string;
	publishAsBlog: boolean;
	shouldNotify: boolean;
	moveParams: OverrideMovePageParams | undefined;
	restrictionsRestObj: RestrictionsRESTObj | undefined;
};
const DEFAULT_SETTINGS: PublishModalContextSettings = {
	pendingScheduledPublishDate: undefined,
	versionComments: '',
	publishAsBlog: false,
	shouldNotify: true,
	moveParams: undefined,
	restrictionsRestObj: undefined,
};

export type PublishModalContextType = PublishModalContextSettings & {
	currentlyScheduledPublishDate: Date | undefined;
	setCurrentlyScheduledPublishDate: React.Dispatch<React.SetStateAction<Date | undefined>>;
	setPendingScheduledPublishDate: React.Dispatch<React.SetStateAction<Date | undefined>>;
	scheduledPublishDate: Date | undefined;
	setVersionComments: React.Dispatch<React.SetStateAction<string>>;
	setPublishAsBlog: React.Dispatch<React.SetStateAction<boolean>>;
	expanded: boolean;
	setExpanded: React.Dispatch<React.SetStateAction<boolean>>;
	setShouldNotify: React.Dispatch<React.SetStateAction<boolean>>;
	setRestrictionsRestObj: React.Dispatch<React.SetStateAction<RestrictionsRESTObj | undefined>>;
	hasModifiedRestrictions: boolean;
	refetchAll: () => void;
	shouldNotifyComputedValue: boolean;
	discardCurrentState: () => void;
	graphqlError: ApolloErrorInterop | undefined;
	restrictionsGraphQLError: Error | undefined;
	onClickPublish: (arg: PublishProps) => any;
	onClickPreview: (...args: any) => any;
	retrieveEditorTitleAndContent: RetrieveEditorTitleAndContent;
	flags: FlagsStateContainer;
	hasModifiedSettings: boolean;
	isBlog: boolean;
	setIsBlog: React.Dispatch<React.SetStateAction<boolean>>;
	setMoveParams: React.Dispatch<React.SetStateAction<OverrideMovePageParams | undefined>>;
	moveParams: OverrideMovePageParams | undefined;
	hasModifiedLocation: boolean;
	hasScheduledRestrictions: boolean;
	setHasScheduledRestrictions: React.Dispatch<React.SetStateAction<boolean>>;
	isConvertToPageDialog: boolean;
};

export const defaultRetrieveEditorTitleAndContent: RetrieveEditorTitleAndContent = () =>
	Promise.resolve({
		title: '',
		adf: { type: '' },
	});

// These default values are never used, but we have to provide them in case
// a component decides to consume PublishModalContext without a parent providing the context.
export const PublishModalContext = createContext<PublishModalContextType>({
	...DEFAULT_SETTINGS,
	currentlyScheduledPublishDate: undefined,
	setCurrentlyScheduledPublishDate: () => {},
	setPendingScheduledPublishDate: () => {},
	scheduledPublishDate: undefined,
	setVersionComments: () => {},
	setPublishAsBlog: () => {},
	expanded: false,
	setExpanded: () => {},
	setShouldNotify: () => {},
	setRestrictionsRestObj: () => {},
	hasModifiedRestrictions: false,
	refetchAll: () => {},
	shouldNotifyComputedValue: true,
	discardCurrentState: () => {},
	graphqlError: undefined,
	restrictionsGraphQLError: undefined,
	onClickPublish: () => {},
	onClickPreview: () => {},
	retrieveEditorTitleAndContent: defaultRetrieveEditorTitleAndContent,
	flags: new FlagsStateContainer([]),
	hasModifiedSettings: true,
	isBlog: false,
	setIsBlog: () => {},
	setMoveParams: () => {},
	hasModifiedLocation: false,
	hasScheduledRestrictions: false,
	setHasScheduledRestrictions: () => {},
	isConvertToPageDialog: false,
});

export const usePublishModalContextStates = () => {
	const [currentlyScheduledPublishDate, setCurrentlyScheduledPublishDate] = useState<
		Date | undefined
	>(undefined);
	const [pendingScheduledPublishDate, setPendingScheduledPublishDate] = useState<Date | undefined>(
		DEFAULT_SETTINGS.pendingScheduledPublishDate,
	);
	const [versionComments, setVersionComments] = useState<string>(DEFAULT_SETTINGS.versionComments);
	const [publishAsBlog, setPublishAsBlog] = useState<boolean>(DEFAULT_SETTINGS.publishAsBlog);
	const [expanded, setExpanded] = useState<boolean>(false);
	const [shouldNotify, setShouldNotify] = useState<boolean>(DEFAULT_SETTINGS.shouldNotify);
	// undefined indicates that no user-initiated changes have been mode to restrictions
	const [restrictionsRestObj, setRestrictionsRestObj] = useState<RestrictionsRESTObj | undefined>(
		DEFAULT_SETTINGS.restrictionsRestObj,
	);
	const [savedSnapshot, setSavedSnapshot] = useState<PublishModalContextSettings>(DEFAULT_SETTINGS);
	const [hasScheduledRestrictions, setHasScheduledRestrictions] = useState<boolean>(false);
	const [isBlog, setIsBlog] = useState<boolean>(false);
	const [moveParams, setMoveParams] = useState<OverrideMovePageParams | undefined>(
		DEFAULT_SETTINGS.moveParams,
	);

	// When the publish modal opens and a date is already scheduled on the backend,
	// the pendingScheduledPublishDate is set to undefined to indicate there is
	// no date picked by the user, while the currentlyScheduledPublishDate will
	// have the actualy scheduled date value.
	// This check prevents that situation from triggering the discard dialog.
	const hasModifiedDate =
		currentlyScheduledPublishDate?.getTime() !== pendingScheduledPublishDate?.getTime() &&
		pendingScheduledPublishDate !== undefined;
	const hasModifiedRestrictions =
		restrictionsRestObj !== undefined &&
		!isEqual(savedSnapshot.restrictionsRestObj, restrictionsRestObj);
	const hasModifiedLocation =
		savedSnapshot.publishAsBlog !== publishAsBlog || savedSnapshot.moveParams !== moveParams;
	const hasModifiedSettings =
		hasModifiedDate ||
		savedSnapshot.versionComments !== versionComments ||
		savedSnapshot.shouldNotify !== shouldNotify ||
		hasModifiedLocation ||
		hasModifiedRestrictions;

	/* Function to discard current state on modal close */
	const discardCurrentState = () => {
		setPendingScheduledPublishDate(DEFAULT_SETTINGS.pendingScheduledPublishDate);
		setVersionComments(DEFAULT_SETTINGS.versionComments);
		setPublishAsBlog(DEFAULT_SETTINGS.publishAsBlog);
		setShouldNotify(DEFAULT_SETTINGS.shouldNotify);
		setMoveParams(DEFAULT_SETTINGS.moveParams);
		setRestrictionsRestObj(DEFAULT_SETTINGS.restrictionsRestObj);
		setSavedSnapshot(DEFAULT_SETTINGS);
	};

	const scheduledPublishDate = pendingScheduledPublishDate ?? currentlyScheduledPublishDate;

	return {
		currentlyScheduledPublishDate,
		setCurrentlyScheduledPublishDate,
		pendingScheduledPublishDate,
		setPendingScheduledPublishDate,
		versionComments,
		setVersionComments,
		publishAsBlog,
		setPublishAsBlog,
		expanded,
		setExpanded,
		shouldNotify,
		setShouldNotify,
		restrictionsRestObj,
		setRestrictionsRestObj,
		hasModifiedRestrictions,
		scheduledPublishDate,
		discardCurrentState,
		savedSnapshot,
		setSavedSnapshot,
		hasModifiedSettings,
		isBlog,
		setIsBlog,
		setMoveParams,
		moveParams,
		hasModifiedLocation,
		hasScheduledRestrictions,
		setHasScheduledRestrictions,
	};
};

type PublishModalContextProviderProps = {
	children: React.ReactNode;
	onClickPublish: (arg: PublishProps) => any;
	onClickPreview: (...args: any) => any;
	retrieveEditorTitleAndContent: RetrieveEditorTitleAndContent;
	flags: FlagsStateContainer;
	isUnpublishedDraft: boolean;
	contentType: string;
	isConvertToPageDialog?: boolean;
};
export const PublishModalContextProvider = withFlags(
	({
		children,
		onClickPublish,
		onClickPreview,
		retrieveEditorTitleAndContent,
		flags,
		isUnpublishedDraft,
		contentType,
		isConvertToPageDialog = false,
	}: PublishModalContextProviderProps) => {
		const [contentId] = usePageContentId();

		/* State declarations */
		const {
			currentlyScheduledPublishDate,
			setCurrentlyScheduledPublishDate,
			pendingScheduledPublishDate,
			setPendingScheduledPublishDate,
			versionComments,
			setVersionComments,
			publishAsBlog,
			setPublishAsBlog,
			expanded,
			setExpanded,
			shouldNotify,
			setShouldNotify,
			restrictionsRestObj,
			setRestrictionsRestObj,
			hasModifiedRestrictions,
			scheduledPublishDate,
			discardCurrentState,
			savedSnapshot,
			setSavedSnapshot,
			hasModifiedSettings,
			isBlog,
			setIsBlog,
			setMoveParams,
			moveParams,
			hasModifiedLocation,
			hasScheduledRestrictions,
			setHasScheduledRestrictions,
		} = usePublishModalContextStates();

		const onSchedulePublishPubsubEvent = useCallback(
			(_: any, payload: any) => {
				if (
					!['DUAL', 'GRAPHQL'].includes(
						StatsigConfigurations.getString('confluence-migrate-fps-to-graphql-subscription', ''),
					) ||
					!expVal('cc_fps_migration_scheduled-publish', 'isEnabled', false)
				) {
					const {
						schedulePublishRequestBody: { publishTime, eventType },
					} = payload;
					if (eventType === 'scheduled') {
						if (
							!currentlyScheduledPublishDate ||
							currentlyScheduledPublishDate?.toISOString() !== publishTime
						) {
							setCurrentlyScheduledPublishDate(new Date(publishTime));
						}
					} else if (eventType === 'unscheduled') {
						if (currentlyScheduledPublishDate) {
							setCurrentlyScheduledPublishDate(undefined);
						}
					}
				}
			},
			[currentlyScheduledPublishDate, setCurrentlyScheduledPublishDate],
		);

		const onSchedulePublishSubscriptionEvent = useCallback(
			(_delta: string, response: any) => {
				const { eventType, publishTime } = response?.schedulePublished || {};

				if (eventType === 'SCHEDULED') {
					if (
						!currentlyScheduledPublishDate ||
						currentlyScheduledPublishDate?.toISOString() !== publishTime
					) {
						setCurrentlyScheduledPublishDate(new Date(publishTime));
					}
				} else if (eventType === 'UNSCHEDULED') {
					if (currentlyScheduledPublishDate) {
						setCurrentlyScheduledPublishDate(undefined);
					}
				}
			},
			[currentlyScheduledPublishDate, setCurrentlyScheduledPublishDate],
		);

		usePubSubEvent({
			contentId: contentId ?? '',
			contentType: contentType ?? 'page',
			eventName: contentType === 'blogpost' ? 'schedulePublish:blogpost' : 'schedulePublish:page',
			onEvent: onSchedulePublishPubsubEvent,
			skip:
				// eslint-disable-next-line confluence-feature-gating/no-preconditioning
				expVal('cc_fps_migration_scheduled-publish', 'isEnabled', false) &&
				fg('confluence_enable_subscriptions-context-provider') &&
				StatsigConfigurations.getString('confluence-migrate-fps-to-graphql-subscription', '') ===
					'GRAPHQL',
		});

		useSubscriptionEvent({
			contentId: contentId ?? '',
			contentType: contentType ?? 'page',
			subscriberId: 'PublishModalContextSubscription',
			delta: 'schedulePublished',
			skip:
				// eslint-disable-next-line confluence-feature-gating/no-preconditioning
				!expVal('cc_fps_migration_scheduled-publish', 'isEnabled', false) ||
				!fg('confluence_enable_subscriptions-context-provider') ||
				!['DUAL', 'GRAPHQL'].includes(
					StatsigConfigurations.getString('confluence-migrate-fps-to-graphql-subscription', ''),
				),
			onDataReceived: onSchedulePublishSubscriptionEvent,
		});

		const { error: restrictionsGraphQLError, refetch: refetchRestrictions } =
			useFetchRestrictionsRESTObj({
				contentId: contentId ?? '',
				onSuccess: (newRestrictions) =>
					// Snapshot should match scheduled restrictions instead of true restrictions
					!hasScheduledRestrictions &&
					setSavedSnapshot({
						...savedSnapshot,
						restrictionsRestObj: newRestrictions,
					}),
			});

		const isFirstTimeBlogPost = isUnpublishedDraft && isBlog;
		let shouldNotifyComputedValue = shouldNotify;
		const computedRestrictions = hasModifiedRestrictions
			? restrictionsRestObj
			: savedSnapshot.restrictionsRestObj;
		if (computedRestrictions && restrictionsArePrivateToYouOnly(computedRestrictions)) {
			shouldNotifyComputedValue = false;
		} else if (publishAsBlog || isFirstTimeBlogPost) {
			shouldNotifyComputedValue = true;
		}

		const loadQueryDataIntoPublishModalState = (newQueryData) => {
			let nextCurrentlyScheduledPublishDate;
			let nextPendingScheduledPublishDate;
			let nextExpanded = expanded;
			let nextShouldNotify;
			let nextPublishAsBlog = publishAsBlog;
			let nextRestrictionsRestObj = restrictionsRestObj;
			let nextmoveParams = moveParams;

			const schedulePublishInfo = newQueryData?.singleContent?.schedulePublishInfo;
			if (schedulePublishInfo?.date) {
				nextCurrentlyScheduledPublishDate = new Date(Date.parse(schedulePublishInfo?.date));
				nextPendingScheduledPublishDate = undefined;
				nextExpanded = true;
			} else {
				nextCurrentlyScheduledPublishDate = undefined;
				nextPendingScheduledPublishDate = undefined;
			}

			const nextVersionComments = schedulePublishInfo?.versionComment ?? '';

			if (typeof schedulePublishInfo?.minorEdit === 'boolean') {
				// if minorEdit==false then we should notify
				nextShouldNotify = !schedulePublishInfo?.minorEdit;
			} else {
				// if we don't have a value for this, set to true which is the default
				nextShouldNotify = true;
			}

			nextPublishAsBlog =
				schedulePublishInfo?.targetType === 'blogpost' &&
				newQueryData?.singleContent?.type !== 'blogpost';

			const targetLocation = schedulePublishInfo?.targetLocation;
			if (targetLocation?.destinationSpace?.key) {
				nextmoveParams = {
					newSpaceKey:
						targetLocation?.destinationSpace?.alias || targetLocation?.destinationSpace?.key,
				};
				if (targetLocation?.parentId) {
					nextmoveParams = {
						...nextmoveParams,
						newParentPageId: targetLocation?.parentId,
					};
				}
			}

			if (schedulePublishInfo?.restrictions) {
				setHasScheduledRestrictions(true);
				nextRestrictionsRestObj = convertRestrictionsObjFromSchedulePublishInfo(
					schedulePublishInfo?.restrictions,
				);
			}

			setCurrentlyScheduledPublishDate(nextCurrentlyScheduledPublishDate);
			setPendingScheduledPublishDate(nextPendingScheduledPublishDate);
			setExpanded(nextExpanded);
			setVersionComments(nextVersionComments);
			setShouldNotify(nextShouldNotify);
			setPublishAsBlog(nextPublishAsBlog);
			setRestrictionsRestObj(nextRestrictionsRestObj);
			setMoveParams(nextmoveParams);

			// TODO: GENI-2751 - Read staged locations from schedule publish info
			// and stage to snapshot with moveParams
			setSavedSnapshot((oldSavedSnapshot) => ({
				pendingScheduledPublishDate: nextPendingScheduledPublishDate,
				versionComments: nextVersionComments,
				publishAsBlog: nextPublishAsBlog,
				shouldNotify: nextShouldNotify,
				moveParams: nextmoveParams,
				// If savedSnapshot.restrictionsRestObj has just been set by useFetchRestrictionsRESTObj, we need to not override what it just set.
				restrictionsRestObj: nextRestrictionsRestObj ?? oldSavedSnapshot.restrictionsRestObj,
			}));
		};

		/* Fetching already scheduled publish data */
		const {
			data: queryData,
			error: graphqlError,
			refetch: refetchPublishModalQuery,
		} = useQuery<
			PublishModalContextPublishModalWithMoreOptionsQuery$data,
			PublishModalContextPublishModalWithMoreOptionsQuery$variables
		>(
			gql`
				query PublishModalContextPublishModalWithMoreOptionsQuery($contentId: ID) {
					singleContent(id: $contentId) {
						id
						type
						space {
							id
						}
						schedulePublishInfo {
							date
							versionComment
							minorEdit
							targetType
							targetLocation {
								parentId
								destinationSpace {
									id
									key
									alias
								}
							}
							restrictions {
								update {
									user: personConnection {
										edges {
											node {
												email
												permissionType
												displayName
												accountId
												publicName
												profilePicture {
													path
												}
											}
										}
									}
									group {
										edges {
											node {
												name
												id
											}
										}
									}
								}
								read {
									user: personConnection {
										edges {
											node {
												email
												permissionType
												displayName
												accountId
												publicName
												profilePicture {
													path
												}
											}
										}
									}
									group {
										edges {
											node {
												name
												id
											}
										}
									}
								}
							}
						}
					}
				}
			`,
			{
				errorPolicy: 'all',
				fetchPolicy: 'cache-and-network',
				variables: {
					contentId,
				},
				onCompleted: loadQueryDataIntoPublishModalState,
			},
		);
		useEffect(() => {
			const schedulePublishDateFromQuery = queryData?.singleContent?.schedulePublishInfo?.date;
			if (schedulePublishDateFromQuery) {
				const newSchedulePublishDateFromQuery = new Date(Date.parse(schedulePublishDateFromQuery));
				setCurrentlyScheduledPublishDate(newSchedulePublishDateFromQuery);
			}
		}, [queryData?.singleContent?.schedulePublishInfo?.date, setCurrentlyScheduledPublishDate]);

		const resetToInitialState = () => {
			if (queryData?.singleContent?.schedulePublishInfo?.date) {
				// If this page is currently scheduled to be published,
				// then load the settings of the current scheduled publish from the backend.
				loadQueryDataIntoPublishModalState(queryData);
			} else {
				discardCurrentState();
			}
		};

		const refetchAll = useCallback(() => {
			void refetchPublishModalQuery();
			refetchRestrictions();
		}, [refetchPublishModalQuery, refetchRestrictions]);

		/* Objects that are provided via the context */
		const contextProvidedData = {
			currentlyScheduledPublishDate,
			setCurrentlyScheduledPublishDate,
			pendingScheduledPublishDate,
			setPendingScheduledPublishDate,
			scheduledPublishDate,
			versionComments,
			setVersionComments,
			publishAsBlog,
			setPublishAsBlog,
			expanded,
			setExpanded,
			shouldNotify,
			setShouldNotify,
			restrictionsRestObj: computedRestrictions,
			setRestrictionsRestObj,
			hasModifiedRestrictions,
			refetchAll,
			shouldNotifyComputedValue,
			graphqlError,
			restrictionsGraphQLError,
			discardCurrentState: resetToInitialState,
			onClickPublish,
			onClickPreview,
			retrieveEditorTitleAndContent,
			flags,
			hasModifiedSettings,
			isBlog,
			setIsBlog,
			setMoveParams,
			moveParams,
			hasModifiedLocation,
			hasScheduledRestrictions,
			setHasScheduledRestrictions,
			isConvertToPageDialog,
		};
		return (
			<PublishModalContext.Provider value={contextProvidedData}>
				{children}
			</PublishModalContext.Provider>
		);
	},
);

export type PublishModalContextProviderExternalProps = {
	onClickPublish: (arg: PublishProps) => any;
	onClickPreview: (...args: any) => any;
	retrieveEditorTitleAndContent: RetrieveEditorTitleAndContent;
	isUnpublishedDraft: boolean;
	contentType: string;
	isConvertToPageDialog?: boolean;
};

type WrappedComponentRequiredProps = {
	isUnpublishedDraft: boolean;
	contentType: string;
};

export const withPublishModalContextProvider =
	<T extends {}>(
		WrappedComponent: React.ComponentType<T & WrappedComponentRequiredProps>,
	): FC<T & PublishModalContextProviderExternalProps> =>
	({
		onClickPublish,
		onClickPreview,
		retrieveEditorTitleAndContent,
		isUnpublishedDraft,
		contentType,
		isConvertToPageDialog,
		...props
	}) => {
		return (
			<PublishModalContextProvider
				onClickPublish={onClickPublish}
				onClickPreview={onClickPreview}
				retrieveEditorTitleAndContent={retrieveEditorTitleAndContent}
				isUnpublishedDraft={isUnpublishedDraft}
				contentType={contentType}
				isConvertToPageDialog={isConvertToPageDialog}
			>
				<WrappedComponent
					isUnpublishedDraft={isUnpublishedDraft}
					contentType={contentType}
					{...(props as unknown as T)}
				/>
			</PublishModalContextProvider>
		);
	};
